import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOnboarding } from "@/hooks/useOnboarding";
import { 
  CheckCircle2, 
  Circle, 
  ChevronDown, 
  ChevronUp, 
  X,
  FileText,
  CheckSquare,
  Archive,
  BarChart3,
  Sparkles,
  ArrowRight
} from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

interface OnboardingChecklistProps {
  className?: string;
}

export function OnboardingChecklist({ className }: OnboardingChecklistProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { 
    state, 
    checklistItems, 
    completedCount, 
    totalCount, 
    isChecklistComplete,
    dismissChecklist 
  } = useOnboarding();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const hasTriggeredConfetti = useRef(false);

  // Trigger confetti when checklist is completed
  useEffect(() => {
    if (isChecklistComplete && !hasTriggeredConfetti.current && !state.checklistDismissed) {
      hasTriggeredConfetti.current = true;
      setShowCelebration(true);
      
      // Fire celebratory confetti burst
      const duration = 2500;
      const end = Date.now() + duration;

      // Initial burst
      confetti({
        particleCount: 80,
        spread: 100,
        origin: { x: 0.5, y: 0.6 },
        colors: ['#667eea', '#764ba2', '#10b981', '#fbbf24', '#ec4899']
      });

      // Continuous side streams
      const frame = () => {
        confetti({
          particleCount: 2,
          angle: 60,
          spread: 45,
          origin: { x: 0, y: 0.7 },
          colors: ['#667eea', '#764ba2', '#10b981']
        });
        confetti({
          particleCount: 2,
          angle: 120,
          spread: 45,
          origin: { x: 1, y: 0.7 },
          colors: ['#667eea', '#764ba2', '#fbbf24']
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();

      // Auto-dismiss after celebration
      setTimeout(() => {
        setShowCelebration(false);
        dismissChecklist();
      }, 4000);
    }
  }, [isChecklistComplete, state.checklistDismissed, dismissChecklist]);

  // Don't show if dismissed (but allow celebration screen)
  if (state.checklistDismissed && !showCelebration) {
    return null;
  }

  // Show celebration card when complete
  if (showCelebration) {
    return (
      <Card className={cn(
        "relative border-primary/40 overflow-hidden",
        "bg-gradient-to-br from-primary/15 via-primary/8 to-accent/10",
        "shadow-[0_0_30px_-5px_hsl(var(--primary)/0.3)]",
        className
      )}>
        {/* Animated glow background */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-primary/20"
          animate={{ 
            x: ["-100%", "100%"],
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        <CardContent className="relative py-8 px-6 text-center">
          {/* Animated icon with pulse */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", damping: 8, stiffness: 100 }}
            className="relative inline-block"
          >
            <motion.div
              className="absolute inset-0 bg-primary/30 rounded-full blur-xl"
              animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            <div className="relative bg-gradient-to-br from-primary to-primary/80 rounded-full p-3">
              <Sparkles className="h-8 w-8 text-primary-foreground" />
            </div>
          </motion.div>
          
          <motion.h3 
            className="text-xl font-bold bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent mt-4 mb-2"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            {t("onboarding.checklist.complete")}
          </motion.h3>
          
          <motion.p 
            className="text-sm text-muted-foreground max-w-[200px] mx-auto"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {t("onboarding.checklist.completeMessage")}
          </motion.p>

          {/* Completion checkmarks */}
          <motion.div 
            className="flex justify-center gap-2 mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.7 + i * 0.1, type: "spring" }}
              >
                <CheckCircle2 className="h-5 w-5 text-primary" />
              </motion.div>
            ))}
          </motion.div>
        </CardContent>
      </Card>
    );
  }

  const progressPercent = (completedCount / totalCount) * 100;

  const getItemIcon = (key: string) => {
    switch (key) {
      case 'hasCreatedFirstOrder':
        return FileText;
      case 'hasMarkedOrderReady':
        return CheckSquare;
      case 'hasViewedArchive':
        return Archive;
      case 'hasViewedDashboard':
        return BarChart3;
      default:
        return Circle;
    }
  };

  const getItemLabel = (key: string) => {
    switch (key) {
      case 'hasCreatedFirstOrder':
        return t("onboarding.checklist.createOrder");
      case 'hasMarkedOrderReady':
        return t("onboarding.checklist.markReady");
      case 'hasViewedArchive':
        return t("onboarding.checklist.viewArchive");
      case 'hasViewedDashboard':
        return t("onboarding.checklist.viewDashboard");
      default:
        return key;
    }
  };

  const getItemHint = (key: string) => {
    switch (key) {
      case 'hasCreatedFirstOrder':
        return t("onboarding.checklist.createOrderHint");
      case 'hasMarkedOrderReady':
        return t("onboarding.checklist.markReadyHint");
      case 'hasViewedArchive':
        return t("onboarding.checklist.viewArchiveHint");
      case 'hasViewedDashboard':
        return t("onboarding.checklist.viewDashboardHint");
      default:
        return "";
    }
  };

  const getItemRoute = (key: string) => {
    switch (key) {
      case 'hasCreatedFirstOrder':
        return '/orders';
      case 'hasMarkedOrderReady':
        return '/orders';
      case 'hasViewedArchive':
        return '/archive';
      case 'hasViewedDashboard':
        return '/dashboard';
      default:
        return null;
    }
  };

  const handleItemClick = (key: string, completed: boolean) => {
    if (completed) return;
    const route = getItemRoute(key);
    if (route) {
      navigate(route);
    }
  };

  return (
    <Card className={cn(
      "border-primary/20 bg-gradient-to-br from-primary/5 via-background to-transparent shadow-sm transition-all duration-300",
      "hover:border-primary/30 hover:shadow-md",
      className
    )}>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <motion.span 
              className="text-primary"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              ✨
            </motion.span>
            {t("onboarding.checklist.title")}
            <span className="text-xs text-muted-foreground font-normal ml-1">
              ({completedCount}/{totalCount})
            </span>
          </CardTitle>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 hover:bg-primary/10"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <motion.div
                animate={{ rotate: isExpanded ? 0 : 180 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronUp className="h-4 w-4" />
              </motion.div>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-destructive/10"
              onClick={dismissChecklist}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-3 mt-2">
          <div className="flex-1 relative">
            <Progress value={progressPercent} className="h-2" />
            {/* Progress glow effect */}
            {progressPercent > 0 && (
              <motion.div 
                className="absolute top-0 left-0 h-2 bg-gradient-to-r from-primary/50 to-primary rounded-full blur-sm"
                style={{ width: `${progressPercent}%` }}
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            )}
          </div>
        </div>
      </CardHeader>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="pt-1 pb-3 px-4">
              <ul className="space-y-1">
                {checklistItems.map((item, index) => {
                  const Icon = getItemIcon(item.key);
                  const hint = getItemHint(item.key);
                  const isClickable = !item.completed && getItemRoute(item.key);
                  
                  return (
                    <motion.li
                      key={item.key}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleItemClick(item.key, item.completed)}
                      className={cn(
                        "flex items-start gap-2.5 text-sm py-2 px-2.5 rounded-lg transition-all duration-200",
                        item.completed 
                          ? "text-muted-foreground bg-primary/5" 
                          : "text-foreground hover:bg-primary/10",
                        isClickable && "cursor-pointer group"
                      )}
                    >
                      <motion.div
                        initial={false}
                        animate={item.completed ? { scale: [1, 1.2, 1] } : {}}
                        transition={{ duration: 0.3 }}
                        className="mt-0.5"
                      >
                        {item.completed ? (
                          <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                        ) : (
                          <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0 group-hover:text-primary transition-colors" />
                        )}
                      </motion.div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={cn(
                            "transition-all duration-200",
                            item.completed && "line-through decoration-primary/40"
                          )}>
                            {getItemLabel(item.key)}
                          </span>
                          {isClickable && (
                            <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:text-primary transition-all -translate-x-1 group-hover:translate-x-0" />
                          )}
                        </div>
                        {!item.completed && hint && (
                          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                            {hint}
                          </p>
                        )}
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}